/**
 * useMobileNavigation
 * Tracks which bottom-tab is active based on the current URL path segment.
 * Only active when running on a native Capacitor platform.
 */
import { Capacitor } from '@capacitor/core';
import { useState, useEffect, useCallback } from 'react';

export type MobileTab = 'home' | 'optimizer' | 'jobs' | 'history' | 'me';

// Note: the `optimizer` tab id is preserved (don't rename) so any persisted
// session storage or deep-link still works. The route it points at is now
// `agent` — the legacy `single-optimize` flow has been removed from the UI.
const TAB_ROUTES: Record<MobileTab, string> = {
  home:      'dashboard',
  optimizer: 'agent',
  jobs:      'jobs',
  history:   'history',
  me:        'profile',
};

function pathToTab(hash: string): MobileTab {
  const segment = hash.replace(/^#?\/?/, '').split('/')[0] || 'dashboard';
  if (segment === 'dashboard' || segment === '') return 'home';
  // Both legacy /single-optimize and /optimize URLs map to the optimizer tab,
  // which now points to /agent.
  if (segment === 'single-optimize' || segment === 'optimizer' || segment === 'optimize' || segment === 'agent') return 'optimizer';
  if (segment === 'jobs') return 'jobs';
  if (segment === 'history') return 'history';
  if (segment === 'profile') return 'me';
  return 'home';
}

function currentTab(): MobileTab {
  return pathToTab(window.location.hash || window.location.pathname);
}

export function useMobileNavigation() {
  const isNative = Capacitor.isNativePlatform();
  const [activeTab, setActiveTab] = useState<MobileTab>(currentTab);

  useEffect(() => {
    if (!isNative) return;
    const onPop = () => setActiveTab(currentTab());
    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onPop);
    };
  }, [isNative]);

  const navigateTo = useCallback(
    (tab: MobileTab, onNavigate?: (page: string) => void) => {
      setActiveTab(tab);
      onNavigate?.(TAB_ROUTES[tab]);
    },
    [],
  );

  return { activeTab, navigateTo, isNative, TAB_ROUTES };
}
